import { Button, FormDisplayTableRow } from '@shared/components';
import { Text } from '@shared/components/text';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { Product } from '@shared/generated/graphql';
import { User } from '@shared/types/user';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import { getUserFeatureDisplayName } from '@shared/utils/userFeatures/getUserFeatureDisplayName';
import clsx from 'clsx';
import { ReactElement, useEffect } from 'react';
import { AiOutlineEdit } from 'react-icons/ai';

export type UserFeaturesDisplayProps = StyleProps & {
  onEdit: () => void;
  user?: User;
  isCompact?: boolean;
};

const ROOT = makeRootClassName('UserFeaturesDisplay');
const el = makeElementClassNameFactory(ROOT);

function UserFeaturesDisplay(props: UserFeaturesDisplayProps): ReactElement {
  const { defaultOrgId, me } = useMeContext();
  const userFeatures = props.user?.features?.features || [];
  const { sendToast } = useToast();
  const organization = me?.organizationsDetails?.find(
    (org) => org.id === defaultOrgId
  );
  const hasEmailScanningProduct = organization?.products?.includes(
    Product.EMAIL_SCANNING
  );

  const featureNames = userFeatures
    .filter((f) => f.enabled && defaultOrgId === f.organizationId)
    .filter((f) => f.name !== 'EmailScanning' || hasEmailScanningProduct)
    .map((f) => getUserFeatureDisplayName(f.name))
    .join(', ');

  const featuresForOtherOrgs = userFeatures
    .filter((f) => f.enabled && defaultOrgId !== f.organizationId)
    .map((f) => getUserFeatureDisplayName(f.name));

  useEffect(() => {
    if (featuresForOtherOrgs.length === 0) return;

    sendToast('Unexpected error', {
      description: `User has ${featuresForOtherOrgs.length} feature${
        featuresForOtherOrgs.length > 1 ? 's' : ''
      } enabled for other organizations. This usually implies a Vooma admin that has switched organizations.`,
      variant: 'error',
    });
  }, [featuresForOtherOrgs.length]);

  return (
    <div className={clsx(ROOT, props.className)}>
      <div className={el`content`}>
        <div className={el`user-features`}>
          <Text type="body-md" isHeavy>
            Features
          </Text>
          <Button
            icon={<AiOutlineEdit size={20} />}
            size="small"
            isGhost
            onPress={() => props.onEdit()}
          />
        </div>
        <FormDisplayTableRow
          displayText="Enabled features"
          value={featureNames || 'None'}
        />
      </div>
    </div>
  );
}

export default UserFeaturesDisplay;
